import type {
  CompanyQuickSearchResult,
  QuickSearchSelectionEnrichmentConfig,
  QuickSearchSelectionMetadata,
} from "@/types/QuickSearch";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

export const toCompanyOption = (company: CompanyQuickSearchResult) => {
  let companyLabel =
    company.name + " (Firma) " + ` (${company.customer_id})`;

  if (company.is_parent) {
    companyLabel += " (Parent)";
  }

  return {
    value: company.subject_id,
    label: companyLabel,
    companyId: company.id,
    isParent: company.is_parent,
    hasParent: company.has_parent,
    is_active: company.is_active,
    is_draft: company.draft_mode === true,
    is_company: true,
    selection: company.selection,
  };
};

export const isCompanySubjectSelection = (value: unknown): boolean =>
  isRecord(value) &&
  Object.keys(value).length !== 0 &&
  ("companyId" in value || value.is_company === true);

export const normalizeQuickSearchSelection = (
  result: unknown,
  config: QuickSearchSelectionEnrichmentConfig,
): QuickSearchSelectionMetadata => {
  const rawResult = isRecord(result) ? result : {};
  const rawMetadataValue = rawResult[config.responseField];
  const rawMetadata = isRecord(rawMetadataValue) ? rawMetadataValue : {};
  const context = isRecord(rawMetadata.context)
    ? (rawMetadata.context as QuickSearchSelectionMetadata["context"])
    : {};

  return {
    selectable: rawMetadata[config.booleanField] === true,
    reason: typeof rawMetadata.reason === "string" ? rawMetadata.reason : null,
    context,
  };
};

export const normalizeQuickSearchResponse = (
  data: unknown,
  config: QuickSearchSelectionEnrichmentConfig,
) => {
  if (!isRecord(data)) return data;

  const normalizedData = { ...data };
  for (const resultType of ["companies", "users"]) {
    const results = data[resultType];
    if (!Array.isArray(results)) continue;
    normalizedData[resultType] = results.map((result) => {
      const rawResult = isRecord(result) ? result : {};
      return {
        ...rawResult,
        selection: normalizeQuickSearchSelection(rawResult, config),
      };
    });
  }
  return normalizedData;
};
